import Select, { type ActionMeta, components } from 'react-select'
import { allSelectableOptionsAreSelected, isOptionSelected, SELECT_ALL_ID } from '../../../../core/utils/combobox'

export interface OptionBase {
  id: string | number
  label: string
  isDisabled?: boolean
}

export interface OptionGrouped<T> {
  label: string
  options: T[]
}

export interface ComboboxProps<T> {
  value: T | T[]
  options: T[] | OptionGrouped<T>[]
  onChange: (value: T, actionMeta: ActionMeta<T>) => void
  comboboxLabel: string
  testId: string
  isLoading?: boolean
  placeholder?: string
  isDisabled?: boolean
  isMulti?: boolean
  isClearable?: boolean
  className?: string
  menuPlacement?: 'auto' | 'top' | 'bottom'
  maxSelectedValues?: number
  helperText?: string
}

/**
 * This component is used when the combobox is un multi-value mode.
 * It replaces the standard option component by a component which display a checkbox for to each.
 */
const CustomOption = props => {
  return (
    <div>
      <components.Option {...props}>
        <input type="checkbox" checked={props.isSelected} onChange={() => null} /> <label>{props.label}</label>
      </components.Option>
    </div>
  )
}

/**
 * This component is used when the combobox is un multi-value mode.
 * It uses to not display "Select all" in select-input when the "Select All" option is checked.
 */
const CustomMultiValue = props => {
  return (
    <>
      {props.data.id === SELECT_ALL_ID ? null : (
        <components.MultiValue {...props}>
          <span>{props.data.label}</span>
        </components.MultiValue>
      )}
    </>
  )
}

export const Combobox = <T extends OptionBase>({
  value,
  onChange,
  comboboxLabel,
  options,
  testId,
  isLoading,
  placeholder,
  isDisabled,
  isMulti,
  isClearable,
  className,
  menuPlacement,
  maxSelectedValues,
  helperText
}: ComboboxProps<T>) => {
  return (
    <div data-testid={`mg-combobox-${testId}`} className="mg-combobox">
      <label data-testid={`mg-combobox-${testId}-label`} className="mg-combobox-label">
        {comboboxLabel}
      </label>
      <Select
        value={value}
        onChange={onChange}
        options={options}
        getOptionValue={(option: T) => String(option.id)}
        getOptionLabel={(option: T) => option.label}
        classNamePrefix="mg-combobox"
        placeholder={placeholder}
        isLoading={isLoading}
        isDisabled={isDisabled}
        isMulti={isMulti}
        isClearable={isClearable}
        closeMenuOnSelect={!isMulti}
        className={className}
        menuPlacement={menuPlacement}
        menuPosition={'fixed'}
        hideSelectedOptions={false}
        components={isMulti && { Option: CustomOption, MultiValue: CustomMultiValue }}
        isOptionSelected={(option, selectedValue) => {
          if (option.id === SELECT_ALL_ID) return allSelectableOptionsAreSelected(options, selectedValue)
          return isOptionSelected<T>(option, selectedValue)
        }}
        {...(maxSelectedValues
          ? {
              isOptionDisabled: (option: T) => {
                const isMultiValue = Array.isArray(value)
                const isAlreadySelected = isMultiValue && value.some((v: T) => v.id === option.id)
                const selectedCount = isMultiValue ? value.length : 0
                if (!isMultiValue) return false
                if (isAlreadySelected) return false
                return selectedCount >= maxSelectedValues
              }
            }
          : {})}
      />
      {helperText && <span className="mg-combobox-helper-text">{helperText}</span>}
    </div>
  )
}
